/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridCustomCellProps,
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridNoRecords,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import {format} from 'date-fns';
import {CompositeFilterDescriptor, State} from '@progress/kendo-data-query';
import styles from './NexParkLocationsGrid.module.scss';
import {getter} from '@progress/kendo-react-common';
import ChangeBillableDialog from './ChangeBillableDialog/ChangeBillableDialog';
import {DropDownList} from '@progress/kendo-react-dropdowns';

import ToggleOffIcon from '../../../assets/toggle-off.svg';
import ToggleOnIcon from '../../../assets/toggle-on.svg';
import {
  GridDataResult,
  LocationStatusFilter,
  LocationStatusFilterType,
  LotModel
} from '../../../model/api/nexParkInstance/lotDirectory';
import {
  getNexParkInstanceUrl,
  nexParkInstanceService,
  notifications,
} from '../../../services';
import {Button} from '@progress/kendo-react-buttons';
import {useConfirmation} from '../../../components/common';
import {CSVLink} from 'react-csv';
import {downloadIcon} from '@progress/kendo-svg-icons';
import {useGridColumnPersist} from '../../../hooks/useGridColumnPersist';
import {BillingMode} from '../../../model/api/customers/billing-mode';
import {nexResponseService} from '../../../services/api/nexResponse';
import {NexResponsePriceMode} from '../../../model/api/locations/NexResponsePriceMode';
import {ChangePriceCategoryDialog} from './ChangePriceCategoryDialog/ChangePriceCategoryDialog';
import {usePermissions} from "../../../components/auth/PermissionsProvider";

const DATA_ITEM_KEY = 'id';
const SELECTED_FIELD = 'selected';
const idGetter = getter(DATA_ITEM_KEY);

export enum NexParkGridColumn {
  Selected = 'selected',
  LotNumber = 'LotNumber',
  LotName = 'LotName',
  Id = 'Id',
  City = 'City',
  Street = 'Street',
  Zip = 'Zip',
  Region = 'Region',
  LocationType = 'LocationType',
  Status = 'Status',
  LastUpdate = 'LastUpdate',
  Billable = 'Billable',
  LanesCount = 'LanesCount',
  NexResponsePriceMode = 'NexResponsePriceMode',
}

export enum CommandType {
  SetBillable,
  SetUnbillable,
}

interface LinkCellProps extends GridCustomCellProps {
  instanceUrl: string | null;
}

const priceCategoryOptions = [
  { text: 'Phase 1', value: NexResponsePriceMode.Phase1 },
  { text: 'Phase 2', value: NexResponsePriceMode.Phase2 },
];

interface PriceCategoryCellProps extends GridCustomCellProps {
  customerId: number;
  onActionComplete: (lotId: number, newMode: NexResponsePriceMode) => void;
}

const PriceCategoryCell: FC<PriceCategoryCellProps> = ({ customerId, dataItem, onActionComplete }) => {
  const confirmation = useConfirmation();

  const currentMode = dataItem.nexResponsePriceMode;
  const currentOption = priceCategoryOptions.find((o) => o.value === currentMode) || null;

  const handleChange = async (e: any) => {
    const newOption = e.target.value;
    const newMode = newOption.value;
    const lotName = dataItem.lotName;

    onActionComplete(dataItem.id, newMode);

    try {
      await confirmation({
        message: `Are you sure you want to change Price Category for location "${lotName}"?`,
      });

      await nexResponseService.setLotPriceModelStatus(customerId, dataItem.id, newMode);
      notifications.success(`Price Category updated for ${lotName}.`);
    } catch (error) {
      onActionComplete(dataItem.id, currentMode);
      console.log('Update cancelled');
    }
  };

  return (
    <td>
      <DropDownList data={priceCategoryOptions} textField='text' dataItemKey='value' value={currentOption} onChange={handleChange} />
    </td>
  );
};

const LinkCell: FC<LinkCellProps> = (props) => {
  const { dataItem, field, instanceUrl } = props;
  const textContent = dataItem[field || ''];
  const href = instanceUrl ? `${instanceUrl}/#/locations/${dataItem.id}/configuration` : '#';

  if (!instanceUrl) {
    return <td>{textContent}</td>;
  }

  return (
    <td>
      <a href={href} target='_blank' rel='noopener noreferrer'>
        {textContent}
      </a>
    </td>
  );
};

const StatusCell: FC<GridCellProps> = ({ dataItem }) => {
  const { archived, active } = dataItem;
  const statusText = archived ? 'Archived' : active ? 'Enabled' : 'Disabled';
  return <td>{statusText}</td>;
};

const DateCell: FC<GridCellProps> = ({ dataItem }) => {
  const formattedDate = dataItem.lastUpdate ? format(new Date(dataItem.lastUpdate), 'LLL d yyyy, h:mm:ss a') : 'N/A';
  return <td>{formattedDate}</td>;
};

const TableCell = (props: { dataItem: LotModel; field: keyof LotModel }) => {
  return <td>{props.dataItem[props.field]}</td>;
};

interface ActionsCellProps extends GridCustomCellProps {
  customerId: number | null;
  billingMode: BillingMode;
  onActionComplete?: (lotId: number, newBillableStatus: boolean) => void;
}

const ActionsCell: FC<ActionsCellProps> = ({ customerId, billingMode, dataItem, onActionComplete }) => {
  const { id, billable, lotName } = dataItem;
  const confirmation = useConfirmation();

  const handleChangeBillableClick = async () => {
    if (!customerId) {
      return;
    }

    try {
      await confirmation({
        message: `Are you sure you want to change billable status for location "${lotName}"?`,
      });
      const newBillableStatus = !billable;
      if (billingMode === BillingMode.NexPark) {
        await nexParkInstanceService.setLotBillableStatus(customerId, { lotId: id, isBillable: newBillableStatus });
      } else if (billingMode === BillingMode.NexResponse) {
        await nexResponseService.setLotBillableStatus(customerId, { lotId: id, isBillable: newBillableStatus })
      } else {
        throw new Error(`Unsupported billing mode: ${billingMode}`);
      }

      notifications.success(`Billable status for "${lotName}" has been updated.`);
      onActionComplete?.(id, newBillableStatus);
    } catch (error) {
      console.error('Error setting billable status:', error);
    }
  };

  return (
    <td className={styles.actionsCell}>
      <img
        className={styles.actionIcon}
        src={dataItem.billable ? ToggleOnIcon : ToggleOffIcon}
        title='Change billable status'
        alt='Change billable status'
        onClick={handleChangeBillableClick}
      />
    </td>
  );
};

interface IProps {
  customerId: number;
  customerName: string;
  billingMode: BillingMode;
  regionId: number;
}

export const NexParkLocationsGrid: FC<IProps> = ({ customerId, customerName, billingMode, regionId }) => {
  const [gridData, setGridData] = useState<GridDataResult>({ data: [], total: 0 });
  const [instanceUrl, setInstanceUrl] = useState<string | null>(null);
  const { hasCSVExportAccess } = usePermissions();
  const [loadingState, setLoadingState] = useState({ loading: true, error: null as string | null });

  const [showChangeBillableDialog, setShowChangeBillableDialog] = useState<boolean>(false);
  const [commandType, setCommandType] = useState<CommandType>(CommandType.SetUnbillable);

  const [selectedState, setSelectedState] = useState<{
    [id: number]: boolean;
  }>({});

  const csvLinkRef = useRef<any>(null);
  const [allCsvData, setAllCsvData] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [showPriceCategoryDialog, setShowPriceCategoryDialog] = useState<boolean>(false);

  const [dataState, setDataState] = useState<State>({
    skip: 0,
    take: 20,
    group: [],
    sort: [],
  });

  const [selectedStatus] = useState<LocationStatusFilterType>(LocationStatusFilter.ENABLED);

  const baseColumns = useMemo(
    () => [
      {
        field: NexParkGridColumn.Selected,
        width: 50,
        minWidth: 50,
        title: '',
        filterable: false,
        sortable: false,
        reorderable: false,
      },
      { field: NexParkGridColumn.LotNumber, title: 'Number', width: 150 },
      { field: NexParkGridColumn.LotName, title: 'Name', width: 250 },
      { field: NexParkGridColumn.Id, title: 'Key', width: 200 },
      { field: NexParkGridColumn.City, title: 'City', width: 200 },
      { field: NexParkGridColumn.Street, title: 'Address', width: 200 },
      { field: NexParkGridColumn.Zip, title: 'Zip', width: 200 },
      { field: NexParkGridColumn.Region, title: 'Region', width: 200 },
      { field: NexParkGridColumn.LocationType, title: 'Location Type', width: 220 },
      { field: NexParkGridColumn.Status, title: 'Status', width: 150, filterable: false, sortable: false },
      { field: NexParkGridColumn.LastUpdate, title: 'Last Update', width: 200, filterable: false },
      { field: NexParkGridColumn.Billable, title: 'Billable', width: 150, filter: 'boolean' },
      { field: NexParkGridColumn.NexResponsePriceMode, title: 'Price Category', width: 150, filterable: false },
      { field: NexParkGridColumn.LanesCount, title: 'Active Lanes', width: 200, filterable: false },
    ],
    [],
  );

  const { columns, onColumnResize, onColumnReorder } = useGridColumnPersist(`customer-nexpark-locations-order-and-size`, baseColumns);

  const statusFilter = useMemo((): CompositeFilterDescriptor | undefined => {
    const compositeFilter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [],
    };

    switch (selectedStatus) {
      case LocationStatusFilter.ARCHIVED:
        compositeFilter.filters.push({ field: 'Archived', operator: 'eq', value: 'true' });
        break;
      case LocationStatusFilter.DISABLED:
        compositeFilter.filters.push({ field: 'Active', operator: 'eq', value: 'false' });
        compositeFilter.filters.push({ field: 'Archived', operator: 'eq', value: 'false' });
        break;
      case LocationStatusFilter.ENABLED:
        compositeFilter.filters.push({ field: 'Active', operator: 'eq', value: 'true' });
        break;
      case LocationStatusFilter.ALL:
        break;
    }
    return compositeFilter;
  }, [selectedStatus]);

  useEffect(() => {
    setSelectedState({});
  }, [billingMode, customerId]);

  useEffect(() => {
    const fetchInstanceUrl = async () => {
      if (!customerId || billingMode !== BillingMode.NexPark) {
        setLoadingState({ loading: false, error: null });
        return;
      }

      setLoadingState({ loading: true, error: null });
      try {
        const url = await getNexParkInstanceUrl(customerId);
        if (url == null || url === '') {
          notifications.info("Customer doesn't have a connected NexPark environment url for fetching data.");
        }
        setInstanceUrl(url);
      } catch (error) {
        console.error('Failed to load instance URL:', error);
        setLoadingState({ loading: false, error: 'Failed to load instance URL' });
      } finally {
        setLoadingState((prevState) => ({ ...prevState, loading: false }));
      }
    };

    fetchInstanceUrl();
  }, [customerId, billingMode]);

  const fetchData = useCallback(async () => {
    if (!customerId) {
      setLoadingState({ loading: false, error: null });
      return;
    }

    if (billingMode === BillingMode.NexPark && instanceUrl === null) {
      setLoadingState({ loading: false, error: null });
      return;
    }

    if (billingMode === BillingMode.NexResponse && !regionId) {
      setLoadingState({ loading: false, error: null });
      return;
    }

    setLoadingState({ loading: true, error: null });

    const finalFilter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [...(statusFilter?.filters || []), ...(dataState.filter?.filters || [])],
    };

    const finalState: State = {
      ...dataState,
      filter: finalFilter,
    };

    try {
      if (billingMode === BillingMode.NexPark) {
        const result = await nexParkInstanceService.getLocationsList(customerId, finalState);
        setGridData(result);
      }
      if (billingMode === BillingMode.NexResponse) {
        const result = await nexResponseService.getLocationsList(customerId, regionId, finalState);
        setGridData(result);
      }
    } catch (error) {
      console.error('Failed to fetch locations list:', error);
      setLoadingState({ loading: false, error: 'Failed to load data. Please try again later.' });
      setGridData({ data: [], total: 0 });
    } finally {
      setLoadingState((prevState) => ({ ...prevState, loading: false }));
    }
  }, [dataState, customerId, statusFilter, instanceUrl, billingMode, regionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDataStateChange = useCallback(
      (event: GridDataStateChangeEvent) => {
        const newDataState = { ...event.dataState };

        if (event.targetEvent?.value === 'All') {
          newDataState.take = gridData.total;
          newDataState.skip = 0;
        }

        const hasFilterChanged = newDataState.filter !== dataState.filter;
        const hasSortChanged = newDataState.sort !== dataState.sort;

        if (hasFilterChanged || hasSortChanged) {
          newDataState.skip = 0;
        }

        setDataState(newDataState as State);
      },
      [dataState.filter, dataState.sort, gridData.total]
  );

  const onSelectionChange = useCallback((event: GridSelectionChangeEvent) => {
    const currentItemID = idGetter(event.dataItem);
    setSelectedState((prevState) => {
      const newState = { ...prevState };
      newState[currentItemID] = !prevState[currentItemID];
      return newState;
    });
  }, []);

  const onHeaderSelectionChange = useCallback((event: GridHeaderSelectionChangeEvent) => {
    const checkboxElement = event.syntheticEvent.target as HTMLInputElement;
    const checked = checkboxElement.checked;
    const newSelectedState: { [key: string]: boolean } = {};

    event.dataItems.forEach((item: LotModel) => {
      newSelectedState[idGetter(item)] = checked;
    });
    setSelectedState(newSelectedState);
  }, []);

  const handleMultipleBillableStatusChange = useCallback((lotIds: number[], newBillableStatus: boolean) => {
    const idsToUpdate = new Set(lotIds.map((id) => Number(id)));

    setGridData((currentGridData) => {
      const updatedData = currentGridData.data.map((item) => {
        if (idsToUpdate.has(item.id)) {
          return { ...item, billable: newBillableStatus };
        }
        return item;
      });

      return {
        ...currentGridData,
        data: updatedData,
      };
    });
  }, []);

  const handleBillableStatusChange = (lotId: number, newBillableStatus: boolean) =>
    handleMultipleBillableStatusChange([lotId], newBillableStatus);

  const handleMultiplePriceCategoryChange = useCallback((lotIds: number[], newPriceMode: NexResponsePriceMode) => {
    const idsToUpdate = new Set(lotIds.map((id) => Number(id)));

    setGridData((currentGridData) => ({
      ...currentGridData,
      data: currentGridData.data.map((item) => (idsToUpdate.has(item.id) ? { ...item, nexResponsePriceMode: newPriceMode } : item)),
    }));
  }, []);

  const handlePriceCategoryDialogClose = (result?: { selectedLotIds: number[]; priceMode: NexResponsePriceMode }) => {
    setShowPriceCategoryDialog(false);
    if (result) {
      handleMultiplePriceCategoryChange(result.selectedLotIds, result.priceMode);
      setSelectedState({});
    }
  };

  const openDialog = (type: CommandType) => {
    setCommandType(type);
    setShowChangeBillableDialog(true);
  };

  const handleDialogClose = (result?: { selectedLotIds?: number[] }) => {
    setShowChangeBillableDialog(false);
    if (result?.selectedLotIds && result.selectedLotIds.length > 0) {
      const newBillableStatus = commandType === CommandType.SetBillable;
      handleMultipleBillableStatusChange(result.selectedLotIds, newBillableStatus);
      setSelectedState({});
    }
  };

  const headerCheckboxState = useMemo(() => {
    const currentPageData = gridData.data;
    if (currentPageData.length === 0) {
      return false;
    }

    return currentPageData.every((item) => selectedState[idGetter(item)]);
  }, [gridData.data, selectedState]);

  const handleSelectAllChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setSelectedState((prevState) => {
      const newSelectedState = { ...prevState };
      gridData.data.forEach((item: LotModel) => {
        newSelectedState[idGetter(item)] = checked;
      });
      return newSelectedState;
    });
  }, [gridData.data]);

  const dataWithSelection = useMemo(() => {
    return gridData.data.map((item) => ({
      ...item,
      [SELECTED_FIELD]: selectedState[idGetter(item)],
    }));
  }, [gridData.data, selectedState]);

  const noRecordsMessage = () => {
    if (loadingState.error) return loadingState.error;
    if (loadingState.loading) return 'Loading...';
    if (!instanceUrl) return 'No locations available. Please connect the customer to a NexPark environment.';
    return 'There are no locations to display.';
  };

  const selectedCount = useMemo(() => Object.values(selectedState).filter(Boolean).length, [selectedState]);

  const selectedLotIds = Object.keys(selectedState)
    .filter((Id) => selectedState[+Id])
    .map((Id) => Number(Id));

  const csvHeaders = useMemo(() => {
    return [
      { label: 'Number', key: 'lotNumber' },
      { label: 'Name', key: 'lotName' },
      { label: 'Key', key: 'id' },
      { label: 'City', key: 'city' },
      { label: 'Address', key: 'street' },
      { label: 'Zip', key: 'zip' },
      { label: 'Region', key: 'region' },
      { label: 'Location Type', key: 'locationType' },
      { label: 'Status', key: 'status' },
      { label: 'Last Update', key: 'lastUpdate' },
      { label: 'Billable', key: 'billable' },
    ];
  }, []);

  if (billingMode === BillingMode.NexResponse) {
    csvHeaders.push({
      label: 'Price Category',
      key: 'nexResponsePriceMode',
    });
  }

  csvHeaders.push({
    label: 'Active Lanes',
    key: 'lanesCount',
  });

  const getFileName = () => {
    const dateTime = format(new Date(), 'yyyyMMdd&HHmmss');
    return `${customerName}_locations_${dateTime}.csv`;
  };

  const loadAllDataForCsv = useCallback(async () => {
    if (!customerId || gridData.total === 0) return;

    setIsExporting(true);
    try {
      const finalFilter: CompositeFilterDescriptor = {
        logic: 'and',
        filters: [...(statusFilter?.filters || []), ...(dataState.filter?.filters || [])],
      };

      const exportState: State = {
        ...dataState,
        skip: 0,
        take: gridData.total,
        filter: finalFilter,
      };

      const result = billingMode === BillingMode.NexResponse
        ? await nexResponseService.getLocationsList(customerId, regionId, exportState)
        : await nexParkInstanceService.getLocationsList(customerId, exportState);

      const formattedData = result.data.map((item) => {
        return {
          lotNumber: item.lotNumber,
          lotName: item.lotName,
          id: item.id,
          city: item.city,
          street: item.street,
          zip: item.zip,
          region: item.region,
          locationType: item.locationType,
          status: item.archived ? 'Archived' : item.active ? 'Enabled' : 'Disabled',
          lastUpdate: item.lastUpdate ? format(new Date(item.lastUpdate), 'LLL d yyyy, h:mm:ss a') : 'N/A',
          billable: item.billable ? 'Yes' : 'No',
          nexResponsePriceMode: item.nexResponsePriceMode === NexResponsePriceMode.Phase1 ? 'Phase1' : 'Phase2',
          lanesCount: item.lanesCount,
        };
      });

      setAllCsvData(formattedData);
    } catch (error) {
      console.error('Failed to export data to csv:', error);
      notifications.error('An error occurred while exporting the data.');
    } finally {
      setIsExporting(false);
    }
  }, [billingMode, customerId, dataState, gridData.total, regionId, statusFilter]);

  const handleCsvExport = useCallback(() => {
    if (allCsvData.length === 0) {
      loadAllDataForCsv().then(() => {
        setTimeout(() => {
          csvLinkRef.current?.link.click();
        }, 100);
      });
    } else {
      csvLinkRef.current?.link.click();
    }
  }, [allCsvData, loadAllDataForCsv]);

  useEffect(() => {
    setAllCsvData([]);
  }, [billingMode, customerId, dataState.filter, statusFilter]);

  const gridKey = useMemo(() => columns.map((c) => c.field).join('_'), [columns]);

  const containerRef = useRef<HTMLDivElement>(null);
  
  // inp-198 Close the open filter dropdown when the header scrolls.
  // The built-in Kendo filter cell does not expose a ref to its DropDownList,
  // so we find the open dropdown through the focused element.
  // The focus can be on a child of the dropdown (for example, its arrow button), so we use closest().
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const closeOpenFilterPopup = (event: Event) => {
      if (!(event.target as HTMLElement).classList?.contains('k-grid-header-wrap')) return;

      const active = document.activeElement;
      const combobox = active?.closest('[role="combobox"][aria-expanded="true"]');
      if (active instanceof HTMLElement && combobox && container.contains(combobox)) {
        active.blur();
      }
    };

    container.addEventListener('scroll', closeOpenFilterPopup, true);
    return () => container.removeEventListener('scroll', closeOpenFilterPopup, true);
  }, []);

  return (
    <div className={styles.container} ref={containerRef}>
      <CSVLink data={allCsvData} headers={csvHeaders} filename={getFileName()} ref={csvLinkRef} style={{ display: 'none' }} />
      {showChangeBillableDialog && (
        <ChangeBillableDialog
            customerId={customerId}
            billingMode={billingMode}
            commandType={commandType}
          selectedLotIds={selectedLotIds}
          onClose={handleDialogClose}
        />
      )}

      {showPriceCategoryDialog && (
        <ChangePriceCategoryDialog
          customerId={customerId}
          selectedLotIds={selectedLotIds}
          onClose={handlePriceCategoryDialogClose}
          priceCategoryOptions={priceCategoryOptions}
          singleLotName={selectedLotIds?.length === 1 ? gridData?.data?.find((lot) => lot.id === selectedLotIds[0])?.lotName : undefined}
        />
      )}

      <div className={styles.actionBar}>
        <div className={styles.billableActions}>
          <p>{selectedCount} selected</p>
          <div className={styles.buttonGroup}>
            <Button size='small' disabled={selectedCount === 0} onClick={() => openDialog(CommandType.SetBillable)}>
              Set as Billable
            </Button>
            <Button size='small' disabled={selectedCount === 0} onClick={() => openDialog(CommandType.SetUnbillable)}>
              Unset Billable
            </Button>
            {billingMode === BillingMode.NexResponse && (
              <Button size='small' disabled={selectedCount === 0} onClick={() => setShowPriceCategoryDialog(true)}>
                Set Price Category
              </Button>
            )}
          </div>
        </div>
        {hasCSVExportAccess && <Button
            className={styles.csvButton}
            svgIcon={downloadIcon}
            onClick={handleCsvExport}
            disabled={isExporting || gridData.data.length === 0}
        >
          Export to CSV
        </Button>}

      </div>

      <Grid
        key={gridKey}
        data={dataWithSelection}
        total={gridData.total}
        {...dataState}
        skip={dataState.skip ? dataState.skip : 0}
        take={dataState.take ? dataState.take : 20}
        onDataStateChange={handleDataStateChange}
        pageable={{
          buttonCount: 4,
          info: true,
          type: 'numeric',
          pageSizes: [10, 20, 50, 'All'],
          previousNext: true,
        }}
        sortable={{ mode: 'multiple' }}
        selectable={{
          enabled: true,
          drag: false,
          cell: false,
          mode: 'multiple'
        }}
        className={styles.centerFirstColumn}
        filterable={true}
        dataItemKey={DATA_ITEM_KEY}
        selectedField={SELECTED_FIELD}
        onSelectionChange={onSelectionChange}
        onHeaderSelectionChange={onHeaderSelectionChange}
        style={{ opacity: loadingState.loading ? 0.5 : 1 }}
        reorderable
        resizable
        onColumnReorder={onColumnReorder}
        onColumnResize={onColumnResize}
      >
        <GridNoRecords>{noRecordsMessage()}</GridNoRecords>
        {columns.map((col) => {
          const commonProps = {
            key: col.field,
            field: col.field,
            title: col.title,
            width: col.width,
            minWidth: col.minWidth,
            filterable: col.filterable !== false,
            sortable: col.sortable !== false,
            reorderable: col.reorderable !== false,
            filter: col.filter,
          };

          switch (col.field) {
            case NexParkGridColumn.Selected:
              return (
                  <GridColumn
                      {...commonProps}
                      filterable={true}

                      filterCell={() => (
                          <div
                              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%', padding: '8px 0' }}
                          >
                            <input
                                type="checkbox"
                                className={`${styles.selectAllCheckboxDisplayOn} k-checkbox k-checkbox-md k-rounded-md`}
                                checked={headerCheckboxState}
                                onChange={handleSelectAllChange}
                                title="Select all on page"
                                style={{ cursor: 'pointer' }}
                            />
                          </div>
                      )}
                  />
              );

            case NexParkGridColumn.LotNumber:
              return <GridColumn {...commonProps} cell={(props) => <LinkCell {...props} instanceUrl={instanceUrl} field='lotNumber' />} />;

            case NexParkGridColumn.Status:
              return <GridColumn {...commonProps} cell={StatusCell} />;

            case NexParkGridColumn.LastUpdate:
              return <GridColumn {...commonProps} cell={DateCell} />;

            case NexParkGridColumn.Billable:
              return (
                <GridColumn
                  {...commonProps}
                  cell={(props) => (
                    <ActionsCell {...props} billingMode={billingMode} customerId={customerId} field='id' onActionComplete={handleBillableStatusChange} />
                  )}
                />
              );
            case NexParkGridColumn.NexResponsePriceMode:
              if (billingMode !== BillingMode.NexResponse) {
                return null;
              }
              return (
                <GridColumn
                  {...commonProps}
                  cell={(props) => (
                    <PriceCategoryCell
                      {...props}
                      customerId={customerId}
                      onActionComplete={(id, mode) => handleMultiplePriceCategoryChange([id], mode)}
                    />
                  )}
                />
              );

            case NexParkGridColumn.LotName:
            case NexParkGridColumn.Id:
            case NexParkGridColumn.City:
            case NexParkGridColumn.Street:
            case NexParkGridColumn.Zip:
            case NexParkGridColumn.Region:
            case NexParkGridColumn.LocationType:
            case NexParkGridColumn.LanesCount: {
              const dataField = (col.field.charAt(0).toLowerCase() + col.field.slice(1)) as keyof LotModel;
              return <GridColumn {...commonProps} cell={(props) => <TableCell {...props} field={dataField} />} />;
            }
            default:
              return <GridColumn {...commonProps} />;
          }
        })}
      </Grid>
    </div>
  );
};
